import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/authStore';
import { useUIStore } from '../../stores/uiStore';
import { useSSEStore } from '../../stores/sseStore';
import { useEffect, useState, useCallback, useRef } from 'react';
import { api } from '../../api/client';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { AnalyticsSection } from './AnalyticsSection';
import { HealthBanner } from '../../components/layout/HealthBanner';
import {
  Package, ShoppingCart, DollarSign, TrendingUp, AlertTriangle,
  Database, Radio, Layers, Clock, ArrowUpRight, ArrowDownRight,
  Truck, MessageSquare, BarChart3, RefreshCw, ExternalLink,
  Bell, Star, ChevronDown, Activity, HelpCircle, Zap, Warehouse,
  Play, CheckCircle, XCircle, StopCircle, Loader2
} from 'lucide-react';
import { secureUrl } from '../../utils/url';
import { Can, useCan } from '../../lib/rbac';

const SITE_FLAGS: Record<string, string> = { MLA: '\u{1F1E6}\u{1F1F7}', MLM: '\u{1F1F2}\u{1F1FD}', MLC: '\u{1F1E8}\u{1F1F1}', MCO: '\u{1F1E8}\u{1F1F4}', MLU: '\u{1F1FA}\u{1F1FE}', MLB: '\u{1F1E7}\u{1F1F7}' };

interface DashboardData {
  products: { active_cbt: number; active_site: number; paused: number; under_review: number; total_cbt: number };
  orders: { orders_24h: number; orders_7d: number; orders_30d: number; total_orders: number };
  revenue: { total_revenue: number; revenue_7d: number; revenue_30d: number; total_profit: number; profit_30d: number; orders_with_cost: number };
  claims: { open_claims: number; claims_7d: number; total_claims: number };
  purchases: { pending_purchases: number; ordered_purchases: number; error_purchases: number };
  inventory: { total_skus: number; total_on_hand: number; total_incoming: number; out_of_stock: number; low_stock: number; stock_value: number };
  warehouse: { open_batches: number; items_in_batches: number; items_received: number; items_closed: number; items_packed: number; ready_to_ship: number };
  recent_orders: Array<{
    ml_order_id: number; status: string; paid_amount: number; currency_id: string;
    site_id: string; date_created: string; buyer_nickname: string;
    ship_status: string; ship_substatus: string; item_title: string; thumbnail: string;
  }>;
  recent_claims: Array<{ ml_claim_id: number; status: string; reason_id: string; stage: string; date_created: string; item_title: string; site_id: string }>;
  top_products: Array<{ title: string; thumbnail: string; sales: number; revenue: number; item_id: string }>;
}

interface WidgetsData {
  sales_today: { count: number; revenue: number };
  pending_questions: { count: number; avg_wait_minutes: number };
  queue: Array<{ status: string; cnt: number }>;
  stock_alerts: { count: number };
  recent_activity: Array<{ action: string; module: string; details: any; created_at: string }>;
  top_products: Array<{ title: string; thumbnail: string; sales: number; revenue: number; item_id: string }>;
  warehouse: { open_batches: number; pending_items: number };
  system_connectors: Array<{ service: string; label: string; has_token: boolean; status: string; expires_at: string }>;
}

interface HealthData {
  name: string; version: string; uptime: number; status: string;
  db: { connected: boolean; pool: { total: number; idle: number; waiting: number; max: number } };
  events: { clients: number };
  jobs: { pending: number; running: number; failed: number; dead: number; done_1h: number };
}

interface ProcessRun {
  runId: string;
  processType: string;
  sellerId: number;
  phase: string;
  totalItems: number;
  processedItems: number;
  succeeded: number;
  failed: number;
  stats: Record<string, unknown>;
  startedAt: string;
  resumed: boolean;
}

interface MLNotice {
  id: string;
  label: string;
  description: string;
  highlighted: boolean;
  from_date: string;
  tags: string[];
  actions: Array<{ label: string; url: string }>;
}

function fmt$(n: number | null | undefined): string {
  if (n == null) return '$0';
  if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'ahora';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

export default function Dashboard() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { sellerId } = useUIStore();
  const can = useCan();
  const [data, setData] = useState<DashboardData | null>(null);
  const [health, setHealth] = useState<HealthData | null>(null);
  const [widgets, setWidgets] = useState<WidgetsData | null>(null);
  const [notices, setNotices] = useState<MLNotice[]>([]);
  const [noticePaging, setNoticePaging] = useState<{ total: number } | null>(null);
  const [showAllNotices, setShowAllNotices] = useState(false);
  const [processes, setProcesses] = useState<ProcessRun[]>([]);
  const [loading, setLoading] = useState(true);

  const loadProcesses = useCallback(async () => {
    try {
      const res = await api.get<ProcessRun[]>('/processes/active');
      if (res.success && res.data) setProcesses(res.data as unknown as ProcessRun[]);
    } catch { /* ignore */ }
  }, []);

  const loadData = useCallback(async () => {
    const [summaryRes, healthRes, commsRes, widgetsRes] = await Promise.all([
      api.get<DashboardData>(`/dashboard/summary?seller_id=${sellerId}`),
      api.get<HealthData>('/health'),
      api.get<any>(`/dashboard/communications?seller_id=${sellerId}&limit=20`).catch(() => ({ success: false, data: null })),
      api.get<WidgetsData>(`/dashboard/widgets?seller_id=${sellerId}`).catch(() => ({ success: false, data: null })),
    ]);
    if (summaryRes.success && summaryRes.data) setData(summaryRes.data as unknown as DashboardData);
    if (healthRes.success && healthRes.data) setHealth(healthRes.data as unknown as HealthData);
    if (commsRes.success && commsRes.data) {
      setNotices((commsRes.data as any).notices || []);
      setNoticePaging((commsRes.data as any).paging || null);
    }
    if (widgetsRes.success && widgetsRes.data) setWidgets(widgetsRes.data as unknown as WidgetsData);
    setLoading(false);
  }, [sellerId]);

  useEffect(() => {
    loadData();
    loadProcesses();
    const interval = setInterval(loadData, 60000);
    const procInterval = setInterval(loadProcesses, 15000);
    return () => { clearInterval(interval); clearInterval(procInterval); };
  }, [loadData, loadProcesses]);

  // SSE: refresh dashboard on real-time events
  const { subscribe } = useSSEStore();
  const loadDataRef = useRef(loadData);
  loadDataRef.current = loadData;
  useEffect(() => {
    const refresh = () => loadDataRef.current();
    const refreshProc = () => loadProcesses();
    const unsubs = [
      subscribe('stock:updated', refresh),
      subscribe('stock:adjusted', refresh),
      subscribe('stock:po-created', refresh),
      subscribe('stock:po-confirmed', refresh),
      subscribe('warehouse:item_received', refresh),
      subscribe('warehouse:item_closed', refresh),
      subscribe('warehouse:batch_closed', refresh),
      subscribe('notification', refresh),
      subscribe('process:progress', refreshProc),
      subscribe('process:complete', () => { refresh(); refreshProc(); }),
      subscribe('process:failed', () => { refresh(); refreshProc(); }),
      subscribe('process:cancelled', refreshProc),
    ];
    return () => unsubs.forEach(u => u());
  }, [subscribe]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-[var(--color-text-muted)]">
        <RefreshCw className="w-5 h-5 animate-spin mr-2" /> {t('common.loading')}
      </div>
    );
  }

  const d = data!;
  const margin30d = d.revenue.revenue_30d > 0
    ? ((d.revenue.profit_30d / d.revenue.revenue_30d) * 100).toFixed(1)
    : '0';

  return (
    <div className="space-y-6">
      <HealthBanner />
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-[var(--color-text)] truncate">
            {t('dashboard.welcome')}, {user?.display_name || user?.username || 'Admin'}
          </h1>
          <p className="text-xs sm:text-sm text-[var(--color-text-secondary)] mt-0.5 sm:mt-1">CBT Platform v3.0</p>
        </div>
        <button
          onClick={() => { setLoading(true); loadData(); }}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-hover)] rounded-lg border border-[var(--color-border)] shrink-0"
        >
          <RefreshCw className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>

      {/* V3 Analytics Charts */}
      <AnalyticsSection />

      {/* KPI Cards Row 1 — Business Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          icon={Package}
          label={t('dashboard.products')}
          value={String(d.products.active_cbt)}
          subtitle={`${d.products.active_site} site items`}
          color="primary"
          badge={d.products.under_review > 0 ? `${d.products.under_review} review` : undefined}
          badgeColor="warning"
        />
        <KPICard
          icon={ShoppingCart}
          label={t('dashboard.ordersToday')}
          value={String(d.orders.orders_24h)}
          subtitle={`${d.orders.orders_7d} en 7d · ${d.orders.orders_30d} en 30d`}
          color="success"
        />
        <Can cap="global.view_money">
          <KPICard
            icon={DollarSign}
            label={`${t('dashboard.revenue')} 30d`}
            value={fmt$(d.revenue.revenue_30d)}
            subtitle={`${fmt$(d.revenue.revenue_7d)} en 7d`}
            color="warning"
          />
        </Can>
        <Can cap="global.view_money">
          <KPICard
            icon={TrendingUp}
            label="Profit 30d"
            value={fmt$(d.revenue.profit_30d)}
            subtitle={`${margin30d}% margen`}
            color="info"
            badge={Number(margin30d) >= 25 ? undefined : 'bajo'}
            badgeColor={Number(margin30d) >= 15 ? 'warning' : 'error'}
          />
        </Can>
      </div>

      {/* KPI Cards Row 2 — Operational */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          icon={AlertTriangle}
          label="Claims Abiertos"
          value={String(d.claims.open_claims)}
          subtitle={`${d.claims.claims_7d} en 7d · ${d.claims.total_claims} total`}
          color={d.claims.open_claims > 0 ? 'error' : 'success'}
        />
        <KPICard
          icon={Truck}
          label="Compras Pendientes"
          value={String(d.purchases.pending_purchases)}
          subtitle={`${d.purchases.ordered_purchases} ordenadas`}
          color={d.purchases.error_purchases > 0 ? 'warning' : 'primary'}
          badge={d.purchases.error_purchases > 0 ? `${d.purchases.error_purchases} errores` : undefined}
          badgeColor="error"
        />
        <KPICard
          icon={Package}
          label="Pausados"
          value={String(d.products.paused)}
          subtitle={`de ${d.products.total_cbt} CBT totales`}
          color={d.products.paused > 10 ? 'warning' : 'primary'}
        />
        <KPICard
          icon={BarChart3}
          label="Órdenes c/ Costo"
          value={String(d.revenue.orders_with_cost)}
          subtitle={can('global.view_money') ? `Profit total: ${fmt$(d.revenue.total_profit)}` : 'Profit total: —'}
          color="info"
        />
      </div>

      {/* KPI Cards Row 3 — Inventory & Warehouse */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          icon={Database}
          label={t('dashboard.stockOnHand')}
          value={String(d.inventory.total_on_hand)}
          subtitle={`${d.inventory.total_skus} SKUs${can('global.view_money') ? ` · ${fmt$(d.inventory.stock_value)} valor` : ''}`}
          color={d.inventory.low_stock > 0 ? 'warning' : 'primary'}
          badge={d.inventory.low_stock > 0 ? `${d.inventory.low_stock} low` : undefined}
          badgeColor="warning"
        />
        <KPICard
          icon={Truck}
          label={t('dashboard.incoming')}
          value={String(d.inventory.total_incoming)}
          subtitle={`${d.inventory.out_of_stock} sin stock`}
          color={d.inventory.out_of_stock > 5 ? 'error' : 'info'}
        />
        <KPICard
          icon={Layers}
          label={t('dashboard.warehouse')}
          value={`${d.warehouse.open_batches} batch${d.warehouse.open_batches !== 1 ? 'es' : ''}`}
          subtitle={`${d.warehouse.items_in_batches} items · ${d.warehouse.items_closed} cerrados`}
          color="primary"
        />
        <KPICard
          icon={Truck}
          label={t('dashboard.readyToShip')}
          value={String(d.warehouse.ready_to_ship)}
          subtitle={`${d.warehouse.items_received} recibidos · ${d.warehouse.items_packed} empacados`}
          color={d.warehouse.ready_to_ship > 0 ? 'warning' : 'success'}
        />
      </div>

      {/* Active Processes */}
      {processes.length > 0 && (
        <ActiveProcessesWidget processes={processes} t={t} onRefresh={loadProcesses} />
      )}

      {/* Operations Widget Cards */}
      {widgets && <WidgetCardsGrid widgets={widgets} t={t} />}

      {/* Main content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Orders */}
        <div className="lg:col-span-2 bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)]">
          <div className="px-4 py-3 border-b border-[var(--color-border)] flex items-center justify-between">
            <h2 className="font-semibold text-sm text-[var(--color-text)]">Últimas Órdenes</h2>
            <a href="/sales" className="text-xs text-[var(--color-primary)] hover:underline flex items-center gap-1">
              Ver todas <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <div className="divide-y divide-[var(--color-border)]">
            {d.recent_orders.length === 0 ? (
              <div className="p-6 text-center text-sm text-[var(--color-text-muted)]">{t('common.noData')}</div>
            ) : d.recent_orders.map((order) => (
              <div key={order.ml_order_id} className="flex items-center gap-3 px-4 py-3 hover:bg-[var(--color-surface-hover)] transition-colors">
                {order.thumbnail ? (
                  <img src={secureUrl(order.thumbnail)} alt="" className="w-10 h-10 rounded object-contain bg-white shrink-0" />
                ) : (
                  <div className="w-10 h-10 rounded bg-[var(--color-surface-hover)] flex items-center justify-center shrink-0">
                    <Package className="w-5 h-5 text-[var(--color-text-muted)]" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[var(--color-text)]">{order.item_title || `Orden #${order.ml_order_id}`}</p>
                  <div className="flex items-center gap-2 text-xs text-[var(--color-text-muted)]">
                    <span>{SITE_FLAGS[order.site_id] || ''} {order.site_id}</span>
                    <span>·</span>
                    <span>{order.buyer_nickname}</span>
                    <span>·</span>
                    <span>{timeAgo(order.date_created)}</span>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-medium text-[var(--color-text)]">
                    {can('global.view_money') ? `$${order.paid_amount?.toFixed(2)}` : '—'}
                  </p>
                  <ShipmentBadge status={order.ship_status} substatus={order.ship_substatus} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right column: Claims + Top Products */}
        <div className="space-y-6">
          {/* Open Claims */}
          <div className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)]">
            <div className="px-4 py-3 border-b border-[var(--color-border)] flex items-center justify-between">
              <h2 className="font-semibold text-sm text-[var(--color-text)]">Claims Recientes</h2>
              <a href="/mercadolibre" className="text-xs text-[var(--color-primary)] hover:underline flex items-center gap-1">
                Ver todos <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            {d.recent_claims.length === 0 ? (
              <div className="p-4 text-center text-sm text-[var(--color-text-muted)]">Sin claims recientes</div>
            ) : (
              <div className="divide-y divide-[var(--color-border)]">
                {d.recent_claims.map((claim) => (
                  <div key={claim.ml_claim_id} className="px-4 py-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-mono text-[var(--color-text-muted)]">#{claim.ml_claim_id}</span>
                      <StatusBadge
                        status={claim.status === 'opened' ? 'error' : 'info'}
                        label={claim.status}
                        size="sm"
                      />
                    </div>
                    <p className="text-sm text-[var(--color-text)] mt-1">{claim.reason_id || claim.stage}</p>
                    <p className="text-xs text-[var(--color-text-muted)]">{timeAgo(claim.date_created)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Top Products */}
          <div className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)]">
            <div className="px-4 py-3 border-b border-[var(--color-border)]">
              <h2 className="font-semibold text-sm text-[var(--color-text)]">Top Productos (30d)</h2>
            </div>
            <div className="divide-y divide-[var(--color-border)]">
              {d.top_products.map((p, i) => (
                <div key={p.item_id} className="flex items-center gap-3 px-4 py-2.5">
                  <span className="text-xs font-bold text-[var(--color-text-muted)] w-4">{i + 1}</span>
                  {p.thumbnail ? (
                    <img src={secureUrl(p.thumbnail)} alt="" className="w-8 h-8 rounded object-contain bg-white shrink-0" />
                  ) : (
                    <div className="w-8 h-8 rounded bg-[var(--color-surface-hover)] shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-[var(--color-text)]">{p.title}</p>
                    <p className="text-xs text-[var(--color-text-muted)]">{p.sales} ventas</p>
                  </div>
                  <span className="text-xs font-medium text-[var(--color-success)]">
                    {can('global.view_money') ? fmt$(p.revenue) : '—'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ML Communications */}
      {notices.length > 0 && (
        <div className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)]">
          <div className="px-4 py-3 border-b border-[var(--color-border)] flex items-center justify-between">
            <h2 className="font-semibold text-sm text-[var(--color-text)] flex items-center gap-2">
              <Bell className="w-4 h-4" /> {t('dashboard.communications')}
            </h2>
            {noticePaging && noticePaging.total > 0 && (
              <span className="text-xs text-[var(--color-text-muted)]">{noticePaging.total} {t('dashboard.communicationsTotal')}</span>
            )}
          </div>
          <div className="divide-y divide-[var(--color-border)]">
            {(showAllNotices ? notices : notices.slice(0, 5)).map((notice) => (
              <div key={notice.id} className="px-4 py-3 hover:bg-[var(--color-surface-hover)] transition-colors">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {notice.highlighted && (
                        <Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400 shrink-0" />
                      )}
                      <span className="text-sm font-medium text-[var(--color-text)]">{notice.label}</span>
                      <span className="text-xs text-[var(--color-text-muted)]">{timeAgo(notice.from_date)}</span>
                    </div>
                    <p className="text-xs text-[var(--color-text-secondary)] line-clamp-2">{notice.description}</p>
                    {(notice.tags.length > 0 || notice.actions.length > 0) && (
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        {notice.tags.map((tag, i) => (
                          <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--color-primary-light)] text-[var(--color-primary)] font-medium">
                            {tag}
                          </span>
                        ))}
                        {notice.actions.map((action, i) => (
                          <a
                            key={i}
                            href={action.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 font-medium flex items-center gap-1"
                          >
                            {action.label} <ExternalLink className="w-2.5 h-2.5" />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {notices.length > 5 && !showAllNotices && (
            <div className="px-4 py-2 border-t border-[var(--color-border)]">
              <button
                onClick={() => setShowAllNotices(true)}
                className="text-xs text-[var(--color-primary)] hover:underline flex items-center gap-1"
              >
                {t('dashboard.showMore')} ({notices.length - 5} {t('dashboard.more')}) <ChevronDown className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* System Health — Compact */}
      {health && (
        <div className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)]">
          <div className="px-4 py-3 border-b border-[var(--color-border)] flex items-center justify-between">
            <h2 className="font-semibold text-sm text-[var(--color-text)]">{t('dashboard.systemHealth')}</h2>
            <div className="flex items-center gap-3">
              <StatusBadge status={health.status === 'ok' ? 'ok' : 'error'} label={health.status === 'ok' ? t('system.healthy') : t('system.error')} size="sm" />
              <span className="text-xs text-[var(--color-text-muted)]">
                Uptime: {Math.floor(health.uptime / 3600)}h {Math.floor((health.uptime % 3600) / 60)}m
              </span>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-[var(--color-border)]">
            <div className="flex items-center gap-3 p-3 bg-[var(--color-surface)]">
              <Database size={18} className="text-[var(--color-text-secondary)]" />
              <div className="flex-1">
                <p className="text-xs font-medium text-[var(--color-text)]">{t('system.database')}</p>
                <p className="text-xs text-[var(--color-text-muted)]">Pool: {health.db.pool.total}/{health.db.pool.max}</p>
              </div>
              <StatusBadge status={health.db.connected ? 'ok' : 'error'} label="OK" size="sm" />
            </div>
            <div className="flex items-center gap-3 p-3 bg-[var(--color-surface)]">
              <Radio size={18} className="text-[var(--color-text-secondary)]" />
              <div className="flex-1">
                <p className="text-xs font-medium text-[var(--color-text)]">{t('system.eventBus')}</p>
                <p className="text-xs text-[var(--color-text-muted)]">{health.events.clients} clients</p>
              </div>
              <StatusBadge status="ok" label="OK" size="sm" />
            </div>
            <div className="flex items-center gap-3 p-3 bg-[var(--color-surface)]">
              <Layers size={18} className="text-[var(--color-text-secondary)]" />
              <div className="flex-1">
                <p className="text-xs font-medium text-[var(--color-text)]">{t('system.jobQueue')}</p>
                <p className="text-xs text-[var(--color-text-muted)]">{health.jobs.done_1h} done/1h</p>
              </div>
              <StatusBadge status={health.jobs.failed > 0 ? 'warning' : 'ok'} label={health.jobs.failed > 0 ? `${health.jobs.failed} fail` : 'OK'} size="sm" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Helper Components ── */

function KPICard({ icon: Icon, label, value, subtitle, color, badge, badgeColor }: {
  icon: typeof Package;
  label: string;
  value: string;
  subtitle: string;
  color: 'primary' | 'success' | 'warning' | 'error' | 'info';
  badge?: string;
  badgeColor?: 'warning' | 'error';
}) {
  const colors = {
    primary: { bg: 'bg-[var(--color-primary-light)]', text: 'text-[var(--color-primary)]' },
    success: { bg: 'bg-[var(--color-success-light)]', text: 'text-[var(--color-success)]' },
    warning: { bg: 'bg-[var(--color-warning-light)]', text: 'text-[var(--color-warning)]' },
    error: { bg: 'bg-[var(--color-error-light)]', text: 'text-[var(--color-error)]' },
    info: { bg: 'bg-[var(--color-info-light)]', text: 'text-[var(--color-info)]' },
  };
  const c = colors[color];

  return (
    <div className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] p-4">
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg ${c.bg} flex items-center justify-center shrink-0`}>
          <Icon size={20} className={c.text} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-2xl font-bold text-[var(--color-text)]">{value}</p>
          <p className="text-xs text-[var(--color-text-secondary)] truncate">{label}</p>
          <p className="text-xs text-[var(--color-text-muted)] truncate mt-0.5">{subtitle}</p>
        </div>
        {badge && (
          <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
            badgeColor === 'error' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'
          }`}>
            {badge}
          </span>
        )}
      </div>
    </div>
  );
}

function WidgetCardsGrid({ widgets, t }: { widgets: WidgetsData; t: (k: string) => string }) {
  const queueMap = new Map((widgets.queue || []).map(q => [q.status, q.cnt]));
  const queueTotal = (widgets.queue || []).reduce((s, q) => s + q.cnt, 0);
  const pendingCnt = queueMap.get('pending') || 0;
  const enrichingCnt = queueMap.get('enriching') || 0;
  const readyCnt = queueMap.get('ready') || 0;
  const errorCnt = queueMap.get('error') || 0;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {/* Sales Today */}
      <div className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg bg-[var(--color-success-light)] flex items-center justify-center">
            <DollarSign size={18} className="text-[var(--color-success)]" />
          </div>
          <span className="text-xs font-medium text-[var(--color-text-secondary)]">{t('dash.salesToday')}</span>
        </div>
        <p className="text-2xl font-bold text-[var(--color-text)]">{widgets.sales_today?.count || 0}</p>
        <Can cap="global.view_money">
          <p className="text-sm text-[var(--color-success)] font-medium mt-1">
            {fmt$(widgets.sales_today?.revenue || 0)} {t('dash.revenue').toLowerCase()}
          </p>
        </Can>
      </div>

      {/* Pending Questions */}
      <div className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[var(--color-warning-light)] flex items-center justify-center">
              <HelpCircle size={18} className="text-[var(--color-warning)]" />
            </div>
            <span className="text-xs font-medium text-[var(--color-text-secondary)]">{t('dash.pendingQuestions')}</span>
          </div>
          <a href="/mercadolibre" className="text-[10px] text-[var(--color-primary)] hover:underline">{t('dash.viewAll')}</a>
        </div>
        <p className="text-2xl font-bold text-[var(--color-text)]">{widgets.pending_questions?.count || 0}</p>
        <p className="text-xs text-[var(--color-text-muted)] mt-1">
          {t('dash.avgWait')}: {widgets.pending_questions?.avg_wait_minutes || 0}m
        </p>
      </div>

      {/* Enrichment Pipeline */}
      <div className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[var(--color-info-light)] flex items-center justify-center">
              <Zap size={18} className="text-[var(--color-info)]" />
            </div>
            <span className="text-xs font-medium text-[var(--color-text-secondary)]">{t('dash.pipeline')}</span>
          </div>
          <a href="/publisher" className="text-[10px] text-[var(--color-primary)] hover:underline">{t('dash.viewAll')}</a>
        </div>
        <p className="text-lg font-bold text-[var(--color-text)] mb-2">{queueTotal} total</p>
        {queueTotal > 0 && (
          <div className="space-y-1">
            <div className="flex items-center gap-1.5 h-3 rounded-full overflow-hidden bg-[var(--color-surface-hover)]">
              {pendingCnt > 0 && <div className="h-full bg-gray-400 rounded-full" style={{ width: `${(pendingCnt / queueTotal) * 100}%` }} />}
              {enrichingCnt > 0 && <div className="h-full bg-blue-500 rounded-full" style={{ width: `${(enrichingCnt / queueTotal) * 100}%` }} />}
              {readyCnt > 0 && <div className="h-full bg-green-500 rounded-full" style={{ width: `${(readyCnt / queueTotal) * 100}%` }} />}
              {errorCnt > 0 && <div className="h-full bg-red-500 rounded-full" style={{ width: `${(errorCnt / queueTotal) * 100}%` }} />}
            </div>
            <div className="flex gap-3 text-[10px] text-[var(--color-text-muted)]">
              <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-gray-400" />{pendingCnt} pending</span>
              <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-blue-500" />{enrichingCnt} enriching</span>
              <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-green-500" />{readyCnt} ready</span>
              {errorCnt > 0 && <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-red-500" />{errorCnt} error</span>}
            </div>
          </div>
        )}
      </div>

      {/* Stock Alerts */}
      <div className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
              (widgets.stock_alerts?.count || 0) > 0
                ? 'bg-[var(--color-error-light)]'
                : 'bg-[var(--color-success-light)]'
            }`}>
              <AlertTriangle size={18} className={(widgets.stock_alerts?.count || 0) > 0
                ? 'text-[var(--color-error)]'
                : 'text-[var(--color-success)]'
              } />
            </div>
            <span className="text-xs font-medium text-[var(--color-text-secondary)]">{t('dash.stockAlerts')}</span>
          </div>
          <a href="/purchases?tab=internal-stock" className="text-[10px] text-[var(--color-primary)] hover:underline">{t('dash.viewAll')}</a>
        </div>
        <p className="text-2xl font-bold text-[var(--color-text)]">{widgets.stock_alerts?.count || 0}</p>
        <p className="text-xs text-[var(--color-text-muted)] mt-1">{t('dash.zeroStock')}</p>
      </div>

      {/* System Health */}
      <div className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg bg-[var(--color-primary-light)] flex items-center justify-center">
            <Activity size={18} className="text-[var(--color-primary)]" />
          </div>
          <span className="text-xs font-medium text-[var(--color-text-secondary)]">{t('dash.systemHealth')}</span>
        </div>
        <div className="space-y-1.5">
          {(widgets.system_connectors || []).length === 0 ? (
            <p className="text-xs text-[var(--color-text-muted)]">{t('dash.noData')}</p>
          ) : (widgets.system_connectors || []).map((c, i) => (
            <div key={i} className="flex items-center justify-between">
              <span className="text-xs text-[var(--color-text-secondary)]">{c.service}</span>
              <span className={`w-2 h-2 rounded-full ${c.status === 'ok' ? 'bg-green-500' : 'bg-red-500'}`} />
            </div>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
            <Clock size={18} className="text-purple-500" />
          </div>
          <span className="text-xs font-medium text-[var(--color-text-secondary)]">{t('dash.recentActivity')}</span>
        </div>
        <div className="space-y-1.5 max-h-[120px] overflow-y-auto">
          {(widgets.recent_activity || []).length === 0 ? (
            <p className="text-xs text-[var(--color-text-muted)]">{t('dash.noData')}</p>
          ) : (widgets.recent_activity || []).slice(0, 6).map((a, i) => (
            <div key={i} className="flex items-center justify-between text-[10px]">
              <span className="text-[var(--color-text-secondary)] truncate max-w-[70%]">{a.action} ({a.module})</span>
              <span className="text-[var(--color-text-muted)] shrink-0">{timeAgo(a.created_at)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Top Products 7d */}
      <div className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg bg-yellow-500/10 flex items-center justify-center">
            <Star size={18} className="text-yellow-500" />
          </div>
          <span className="text-xs font-medium text-[var(--color-text-secondary)]">{t('dash.topProducts')} (7d)</span>
        </div>
        <div className="space-y-1.5 max-h-[120px] overflow-y-auto">
          {(widgets.top_products || []).length === 0 ? (
            <p className="text-xs text-[var(--color-text-muted)]">{t('dash.noData')}</p>
          ) : (widgets.top_products || []).map((p, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="text-[10px] font-bold text-[var(--color-text-muted)] w-3">{i + 1}</span>
              {p.thumbnail ? (
                <img src={secureUrl(p.thumbnail)} alt="" className="w-6 h-6 rounded object-contain bg-white shrink-0" />
              ) : (
                <div className="w-6 h-6 rounded bg-[var(--color-surface-hover)] shrink-0" />
              )}
              <span className="text-[10px] text-[var(--color-text)] flex-1 min-w-0 truncate">{p.title}</span>
              <span className="text-[10px] text-[var(--color-success)] font-medium shrink-0">{p.sales}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Warehouse Status */}
      <div className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center">
              <Warehouse size={18} className="text-cyan-500" />
            </div>
            <span className="text-xs font-medium text-[var(--color-text-secondary)]">{t('dash.warehouseStatus')}</span>
          </div>
          <a href="/warehouse" className="text-[10px] text-[var(--color-primary)] hover:underline">{t('dash.viewAll')}</a>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-[var(--color-text-muted)]">{t('dash.openBatches')}</span>
            <span className="text-sm font-bold text-[var(--color-text)]">{widgets.warehouse?.open_batches || 0}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-[var(--color-text-muted)]">{t('dash.pendingItems')}</span>
            <span className="text-sm font-bold text-[var(--color-text)]">{widgets.warehouse?.pending_items || 0}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function ActiveProcessesWidget({ processes, t, onRefresh }: { processes: ProcessRun[]; t: (k: string) => string; onRefresh: () => void }) {
  const handleCancel = async (runId: string) => {
    if (!confirm(t('proc.cancelConfirm'))) return;
    try {
      await api.post(`/processes/${runId}/cancel`, {});
      onRefresh();
    } catch { /* ignore */ }
  };

  return (
    <div className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)]">
      <div className="px-4 py-3 border-b border-[var(--color-border)] flex items-center justify-between">
        <h2 className="font-semibold text-sm text-[var(--color-text)] flex items-center gap-2">
          <Play size={16} className="text-[var(--color-primary)]" />
          {t('proc.activeProcesses')}
          <span className="text-xs font-normal px-1.5 py-0.5 rounded-full bg-blue-500/20 text-blue-400">
            {processes.length}
          </span>
        </h2>
      </div>
      <div className="divide-y divide-[var(--color-border)]">
        {processes.map((p) => {
          const pct = p.totalItems > 0 ? Math.round((p.processedItems / p.totalItems) * 100) : 0;
          const elapsed = Date.now() - new Date(p.startedAt).getTime();
          const elapsedStr = elapsed > 3600000
            ? `${Math.floor(elapsed / 3600000)}h ${Math.floor((elapsed % 3600000) / 60000)}m`
            : elapsed > 60000
            ? `${Math.floor(elapsed / 60000)}m`
            : `${Math.floor(elapsed / 1000)}s`;
          const itemsPerMs = p.processedItems > 0 ? elapsed / p.processedItems : 0;
          const remaining = p.totalItems - p.processedItems;
          const etaSec = itemsPerMs > 0 ? Math.round((remaining * itemsPerMs) / 1000) : 0;
          const etaStr = etaSec > 3600
            ? `${Math.floor(etaSec / 3600)}h ${Math.floor((etaSec % 3600) / 60)}m`
            : etaSec > 60
            ? `${Math.floor(etaSec / 60)}m`
            : etaSec > 0 ? `${etaSec}s` : '';

          const typeLabel = t(`proc.type.${p.processType}`) || p.processType;

          return (
            <div key={p.runId} className="px-4 py-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Loader2 size={14} className="text-blue-400 animate-spin" />
                  <span className="text-sm font-medium text-[var(--color-text)]">{typeLabel}</span>
                  {p.phase && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--color-surface-hover)] text-[var(--color-text-muted)]">
                      {p.phase}
                    </span>
                  )}
                  {p.resumed && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400">
                      {t('proc.resumed')}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-[var(--color-text-muted)]">{elapsedStr}</span>
                  <Can cap="dashboard.cancel_process">
                    <button
                      onClick={() => handleCancel(p.runId)}
                      className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1"
                      title={t('proc.cancel')}
                    >
                      <StopCircle size={14} />
                    </button>
                  </Can>
                </div>
              </div>

              {/* Progress bar */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-2 rounded-full bg-[var(--color-surface-hover)] overflow-hidden">
                  <div
                    className="h-full rounded-full bg-blue-500 transition-all duration-300"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="text-xs font-mono text-[var(--color-text)] w-10 text-right">{pct}%</span>
              </div>

              {/* Stats */}
              <div className="flex items-center gap-4 mt-1.5 text-[10px] text-[var(--color-text-muted)]">
                <span>{p.processedItems}/{p.totalItems} {t('proc.items')}</span>
                {p.succeeded > 0 && (
                  <span className="flex items-center gap-0.5 text-green-400">
                    <CheckCircle size={10} /> {p.succeeded}
                  </span>
                )}
                {p.failed > 0 && (
                  <span className="flex items-center gap-0.5 text-red-400">
                    <XCircle size={10} /> {p.failed}
                  </span>
                )}
                {etaStr && <span>{t('proc.eta')}: ~{etaStr}</span>}
                <span className="text-[var(--color-text-muted)]">Seller {p.sellerId}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ShipmentBadge({ status, substatus }: { status: string; substatus: string }) {
  if (!status) return <span className="text-xs text-[var(--color-text-muted)]">--</span>;

  const map: Record<string, { color: string; label: string }> = {
    delivered: { color: 'text-green-400', label: 'Entregado' },
    shipped: { color: 'text-blue-400', label: 'Enviado' },
    ready_to_ship: { color: 'text-yellow-400', label: 'Listo' },
    handling: { color: 'text-orange-400', label: 'Preparando' },
    pending: { color: 'text-gray-400', label: 'Pendiente' },
    cancelled: { color: 'text-red-400', label: 'Cancelado' },
    not_delivered: { color: 'text-red-400', label: 'No entregado' },
  };

  const m = map[status] || { color: 'text-[var(--color-text-muted)]', label: status };
  return <span className={`text-xs font-medium ${m.color}`}>{m.label}</span>;
}
